import Rest from '../Rest'

/** Paginador do Laravel. `walletEntries` devolve isto, e não uma lista. */
export interface Paginated<T> {
  data: T[]
  current_page: number
  last_page: number
  per_page: number
  total: number
  from: number | null
  to: number | null
}

export interface Balances {
  total: number
  pending: number
  spendable: number
  withdrawable: number
}

export interface UpcomingRelease {
  available_at: string
  amount_cents: number
}

export interface PanelMetrics {
  clicks: number
  conversions: number
  conversion_rate: number
  gmv_cents: number
  commission_total_cents: number
  avg_ticket_cents: number
  active_campaigns: number
  /**
   * `tracked` = contagem das linhas de clique dentro do período.
   * `lifetime` = contador acumulado do link, porque cliques crus são podados em 180 dias.
   * A UI avisa quando é `lifetime`, para não prometer um recorte que não existe.
   */
  clicks_source: 'tracked' | 'lifetime'
}

export interface SeriesPoint {
  date: string
  commission_cents: number
}

export interface FunnelStep {
  /** clicks | sessions | checkout | sales */
  key: string
  count: number
}

export interface ChannelRow {
  /** Código do canal, ou null nos baldes "cupom" e "não identificado". */
  channel: string | null
  key: string
  label: string
  conversions: number
}

export interface DashboardData {
  balances: Balances
  upcoming: UpcomingRelease[]
  metrics: PanelMetrics
  /** Nulo quando não há período selecionado — "desde o início" não tem um "antes". */
  previous: PanelMetrics | null
  series: { current: SeriesPoint[]; previous: SeriesPoint[] }
  funnel: FunnelStep[]
  channels: ChannelRow[]
}

export interface MarketData {
  positioning: {
    /** Nulo quando a base de comparação é pequena demais para o percentil significar algo. */
    percentile: number | null
    position: number | null
    peers: number
    mine: { conversion_rate: number; avg_ticket_cents: number; commission_cents: number }
    platform: { conversion_rate: number; avg_ticket_cents: number; avg_commission_cents: number }
  }
  timing: {
    weekday: Array<{ bucket: number; count: number; pct: number }>
    hour: Array<{ bucket: number; count: number; pct: number }>
  }
  opportunities: Array<{
    id: number
    name: string
    legal_entity: string | null
    commission_type: string
    commission_amount: number
    recent_sales: number
  }>
}

export interface InfluencerLevel {
  id: number
  name: string
  badge_color: string | null
  commission_bonus_pct: number | string
}

export interface SocialProfile {
  id: number
  network: string
  network_label: string
  handle: string
  profile_url: string | null
  followers: number | null
  verified_at: string | null
}

export interface InfluencerProfile {
  id: number
  name: string
  slug: string
  status: string
  bio: string | null
  level: InfluencerLevel | null
  socialProfiles: SocialProfile[]
}

export interface Participation {
  id: number
  campaign_id: number
  status: string
  coupon_code: string | null
  campaign: any
  /** Métricas agregadas — só existem depois do enriquecimento em GET /me/campaigns. */
  metrics?: {
    clicks: number
    conversions: number
    conversion_rate: number
    gmv_cents: number
    commission_cents: number
  }
  starts_at?: string | null
  ends_at?: string | null
  period_elapsed_pct?: number | null
  days_to_end?: number | null
}

export interface TrackingLinkItem {
  id: number
  slug: string
  short_url: string
  clicks_count: number
  product: any | null
  participation: any
  conversions?: number
  conversion_rate?: number
}

export interface WalletEntryItem {
  id: number
  direction: 'CR' | 'DE'
  type: string
  type_label: string
  description: string | null
  amount_cents: number
  signed_amount_cents: number
  spendable_at: string | null
  available_at: string | null
  created_at: string
}

export interface WithdrawalItem {
  id: number
  status: string
  amount_cents: number
  net_cents: number
  requested_at: string
  scheduled_for: string | null
  paid_at: string | null
  pix_key: string | null
  pix_key_type: string | null
  external_reference: string | null
  rejection_reason: string | null
  failure_reason: string | null
  has_receipt?: boolean
}

export interface PixKeyItem {
  id: number
  key_type: string
  masked_key: string
  status: string
  is_default: boolean
}

/** Intervalo de datas no formato que o `CrudTraitApi` do backend coleta. */
export interface PeriodParams {
  'custom_filters[start_date]'?: string
  'custom_filters[end_date]'?: string
}

function toQuery(params: Record<string, any>): string {
  const pairs = Object.keys(params)
    .filter(key => params[key] !== undefined && params[key] !== null && params[key] !== '')
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)

  return pairs.length ? `?${pairs.join('&')}` : ''
}

/**
 * Painel do influencer. Todos os endpoints são escopados ao próprio cadastro
 * pelo backend (via usuário autenticado) — não há id de influencer nas rotas,
 * de propósito.
 */
export default class InfluencerPanelService extends Rest {
  /**
   * @type {String}
   */
  static resource = '/api/v1/me'

  static build(vs: any = null) {
    return new this(this.resource, null, vs)
  }

  profile(): Promise<InfluencerProfile> {
    return this.get('/influencer')
  }

  dashboard(period: PeriodParams = {}): Promise<DashboardData> {
    return this.get(`/influencer/dashboard${toQuery(period)}`)
  }

  /**
   * Comparativo com a plataforma. Endpoint separado do dashboard de propósito: varre
   * todas as comissões (com cache de 1h no backend) e não pode atrasar os saldos.
   */
  market(period: PeriodParams = {}): Promise<MarketData> {
    return this.get(`/influencer/market${toQuery(period)}`)
  }

  myCampaigns(period: PeriodParams = {}): Promise<Participation[]> {
    return this.get(`/campaigns${toQuery(period)}`)
  }

  availableCampaigns(): Promise<any[]> {
    return this.get('/campaigns/available')
  }

  join(campaignId: number) {
    return this.post(`/campaigns/${campaignId}/join`, {})
  }

  leave(participationId: number) {
    return this.put(`/participations/${participationId}/leave`, {})
  }

  links(period: PeriodParams = {}): Promise<TrackingLinkItem[]> {
    return this.get(`/links${toQuery(period)}`)
  }

  /**
   * `channel` gera um link distinto por canal — é o que torna a venda rastreável até a
   * rede onde foi divulgada. Sem canal, o link é o genérico da campanha.
   */
  createLink(participationId: number, productId: number | null = null, channel: string | null = null) {
    return this.post(`/participations/${participationId}/links`, {
      product_id: productId,
      channel,
    })
  }

  /** Material de divulgação da campanha (peças, briefing, hashtags). */
  campaignAssets(participationId: number): Promise<any> {
    return this.get(`/participations/${participationId}/assets`)
  }

  wallet(): Promise<{ wallet: any; balances: Balances; upcoming: UpcomingRelease[] }> {
    return this.get('/wallet')
  }

  walletEntries(params: { page?: number; type?: string; direction?: string } & PeriodParams = {}):
    Promise<Paginated<WalletEntryItem> & { totals?: { inflow_cents: number; outflow_cents: number } }> {
    return this.get(`/wallet/entries${toQuery({ page: 1, ...params })}`)
  }

  withdrawals(): Promise<WithdrawalItem[]> {
    return this.get('/withdrawals')
  }

  requestWithdrawal(amountCents: number, pixKeyId: number | null = null) {
    return this.post('/withdrawals', { amount_cents: amountCents, pix_key_id: pixKeyId })
  }

  cancelWithdrawal(id: number) {
    return this.delete(`/withdrawals/${id}`)
  }

  /** Devolve a URL do comprovante; rejeita quando o admin ainda não anexou. */
  withdrawalReceipt(id: number): Promise<{ id: number; file_name: string; url: string }> {
    return this.get(`/withdrawals/${id}/receipt`)
  }

  pixKeys(): Promise<PixKeyItem[]> {
    return this.get('/pix_keys')
  }

  savePixKey(keyType: string, keyValue: string) {
    return this.post('/pix_keys', { key_type: keyType, key_value: keyValue })
  }

  setDefaultPixKey(id: number) {
    return this.put(`/pix_keys/${id}/default`, {})
  }

  deletePixKey(id: number) {
    return this.delete(`/pix_keys/${id}`)
  }
}
